import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash, ArrowsClockwise, ClipboardText, DownloadSimple, Eye, PencilSimple, LinkSimple, ArrowRight } from "@phosphor-icons/react";
import { formatApiError } from "@/contexts/AuthContext";
import { downloadTemplate, TEMPLATE_HEADERS } from "@/lib/feedTemplates";

const FEED_TYPES = ["csv", "json", "xml"];
const CATEGORIES = ["new-bikes", "used-bikes", "e-bikes", "parts", "accessories"];

export default function AdminFeeds() {
  const [feeds, setFeeds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showLogs, setShowLogs] = useState(null);
  const [form, setForm] = useState({ name: "", url: "", feed_type: "csv", markup: 1.30, default_category: "parts", active: true, dry_run: false, price_change_threshold: 0.10 });
  const [importMode, setImportMode] = useState("feed"); // "feed" | "url"
  const [urlBrand, setUrlBrand] = useState("");
  const [urlSubmitting, setUrlSubmitting] = useState(false);
  const [urlStatus, setUrlStatus] = useState(""); // human-readable job status text

  // Poll a scrape job until it terminates. Matches AdminImport.js semantics.
  const POLL_INTERVAL_MS = 2000;
  const POLL_MAX_MS = 90_000;
  const pollScrapeJob = async (jobId, onTick) => {
    const startedAt = Date.now();
    await new Promise((r) => setTimeout(r, 600));
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - startedAt > POLL_MAX_MS) {
        const err = new Error(`Scrape did not finish within ${POLL_MAX_MS / 1000}s. It may still complete in the background — check the URL Importer page shortly.`);
        err.timeout = true;
        throw err;
      }
      const { data } = await api.get(`/admin/import/status/${jobId}`);
      onTick?.(data);
      if (data.status === "success") return data;
      if (data.status === "failed") {
        const err = new Error(data.error || "Scrape failed with an unknown error.");
        err.jobStatus = data;
        throw err;
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/feeds").then((r) => setFeeds(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createFeed = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/feeds", form);
      toast.success("Feed source added");
      setShowNew(false);
      setForm({ name: "", url: "", feed_type: "csv", markup: 1.30, default_category: "parts", active: true, dry_run: false, price_change_threshold: 0.10 });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const createFromUrl = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) { toast.error("Paste a product URL"); return; }
    setUrlSubmitting(true);
    setUrlStatus("Submitting…");
    try {
      const { data: enq } = await api.post("/admin/import/create", {
        url: form.url.trim(),
        category: form.default_category,
        brand: urlBrand,
        markup_pct: (form.markup - 1) * 100,
      });
      setUrlStatus("Queued — scraping supplier page…");
      const final = await pollScrapeJob(enq.job_id, (tick) => {
        if (tick.status === "queued") setUrlStatus("Queued — waiting for a worker…");
        else if (tick.status === "running") setUrlStatus("Scraping supplier page (rendering with Chromium)…");
      });
      const p = final.result || {};
      if (p.duplicate) {
        toast.success(`Already existed — reused: ${p.name}`);
      } else {
        toast.success(`Imported: ${p.name} (€${p.price?.toFixed(2)} retail with ${((form.markup - 1) * 100).toFixed(0)}% markup)`);
      }
      setShowNew(false);
      setForm({ name: "", url: "", feed_type: "csv", markup: 1.30, default_category: "parts", active: true, dry_run: false, price_change_threshold: 0.10 });
      setUrlBrand("");
      setImportMode("feed");
    } catch (err) {
      const msg = err.timeout ? err.message
        : (err.jobStatus?.error || formatApiError(err.response?.data?.detail) || err.message || "Import failed");
      toast.error(msg);
    } finally {
      setUrlSubmitting(false);
      setUrlStatus("");
    }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete feed "${f.name}"?`)) return;
    try { await api.delete(`/admin/feeds/${f.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const syncNow = async (f) => {
    setSyncing(f.id);
    try {
      const { data } = await api.post(`/admin/feeds/${f.id}/sync`);
      if (data.success) {
        if (data.dry_run) {
          toast.success(`Dry run: would create ${data.would_create_count}, update ${data.would_update_count}`);
        } else {
          toast.success(`Synced: +${data.created} new, ${data.updated} updated`);
        }
      } else {
        toast.error(`Sync failed: ${data.error}`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(null); }
  };

  const previewFeed = async (f) => {
    setPreviewing(f.id);
    setPreview(null);
    try {
      const { data } = await api.post(`/admin/feeds/${f.id}/dry-run`);
      setPreview({ feed: f, ...data });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Preview failed");
      setPreviewing(null);
    }
  };

  const closePreview = () => { setPreview(null); setPreviewing(null); };

  const applyFromPreview = async () => {
    if (!preview) return;
    const f = preview.feed;
    closePreview();
    setSyncing(f.id);
    try {
      const { data } = await api.post(`/admin/feeds/${f.id}/sync`);
      if (data.success) toast.success(`Applied: +${data.created} new, ${data.updated} updated`);
      else toast.error(`Sync failed: ${data.error}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(null); }
  };

  const openLogs = async (f) => {
    setShowLogs(f);
    const { data } = await api.get(`/admin/feeds/${f.id}/logs`);
    setLogs(data);
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-feeds">
      {/* Mode selector — PROMINENT banner separating the two import flows */}
      <div className="mb-8 grid md:grid-cols-2 gap-3" data-testid="import-mode-selector">
        <div className="border-2 border-black bg-white p-5 relative">
          <div className="absolute -top-2 left-3 bg-black text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5">You are here</div>
          <div className="flex items-start gap-3 mt-1">
            <ClipboardText size={22} weight="bold" className="mt-0.5" />
            <div>
              <div className="font-display font-black text-lg leading-tight">Feed Sources</div>
              <div className="text-xs text-neutral-600 mt-1">Bulk import from a supplier's CSV / JSON / XML file. One URL that returns a data feed with hundreds or thousands of rows.</div>
            </div>
          </div>
        </div>

        <Link to="/admin/import" className="border-2 border-[color:var(--vt-orange)] bg-yellow-50 p-5 relative hover:bg-yellow-100 transition-colors group" data-testid="switch-to-url-importer">
          <div className="absolute -top-2 left-3 bg-[color:var(--vt-orange)] text-black text-[10px] uppercase tracking-widest font-bold px-2 py-0.5">Product URL? Go here →</div>
          <div className="flex items-start gap-3 mt-1">
            <LinkSimple size={22} weight="bold" className="mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-black text-lg leading-tight flex items-center gap-2">Product URL Importer <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></div>
              <div className="text-xs text-neutral-700 mt-1">Paste a single product page URL from Bike24, Bike-Discount, Bike-Components, Amazon, eBay, Decathlon, Zalando, Tradeinn or AliExpress. Auto-extracts title, images, price. Applies 30% markup.</div>
            </div>
          </div>
        </Link>
      </div>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-[color:var(--vt-orange)] mb-2">/ SUPPLIER SYNC</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Feed Sources</h1>
          <p className="text-neutral-500 text-sm mt-1">Auto-import products daily at 03:00 UTC · 30% markup applied automatically</p>
          <p className="text-xs text-neutral-500 mt-1"><b>Tip:</b> the URL below must return a raw CSV/JSON/XML file (a data feed) — not a product page. For single product pages use the <Link to="/admin/import" className="text-black font-bold underline">URL Importer</Link>.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="vt-btn-orange text-xs" data-testid="btn-new-feed"><Plus size={14} weight="bold" /> Add Feed Source</button>
      </div>

      <div className="mb-6 bg-white border border-neutral-200 p-6" data-testid="feed-templates">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-lg mb-1">Feed Template</h2>
            <p className="text-xs text-neutral-500 max-w-2xl">
              Give this file to any supplier (Bike24, Bike-Discount, or your own) as the required export format. Every column is optional except <code className="font-mono bg-neutral-100 px-1">name</code>, <code className="font-mono bg-neutral-100 px-1">category</code> and <code className="font-mono bg-neutral-100 px-1">supplier_price</code>. Multiple images use <code className="font-mono bg-neutral-100 px-1">|</code> as separator.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => downloadTemplate("csv")} className="vt-btn-outline text-xs" data-testid="download-csv-template"><DownloadSimple size={14} weight="bold" /> CSV</button>
            <button onClick={() => downloadTemplate("json")} className="vt-btn-outline text-xs" data-testid="download-json-template"><DownloadSimple size={14} weight="bold" /> JSON</button>
            <button onClick={() => downloadTemplate("xml")} className="vt-btn-outline text-xs" data-testid="download-xml-template"><DownloadSimple size={14} weight="bold" /> XML</button>
          </div>
        </div>
        <details className="mt-4 text-xs">
          <summary className="cursor-pointer font-bold uppercase tracking-widest text-neutral-600 hover:text-black">Column reference</summary>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {TEMPLATE_HEADERS.map((h) => (
              <span key={h} className="font-mono bg-neutral-100 border border-neutral-200 px-2 py-1 text-[11px]">{h}</span>
            ))}
          </div>
        </details>
      </div>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        {loading ? <div className="p-12 text-center text-neutral-500">Loading...</div> :
         feeds.length === 0 ? <div className="p-12 text-center text-neutral-500">No feed sources yet. Add one to enable automated product sync.</div> :
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-3 overline text-neutral-600">Name</th>
              <th className="text-left p-3 overline text-neutral-600">Type</th>
              <th className="text-left p-3 overline text-neutral-600">URL</th>
              <th className="text-left p-3 overline text-neutral-600">Default Category</th>
              <th className="text-left p-3 overline text-neutral-600">Mode</th>
              <th className="text-left p-3 overline text-neutral-600">Last Sync</th>
              <th className="text-left p-3 overline text-neutral-600">Status</th>
              <th className="text-right p-3 overline text-neutral-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {feeds.map((f) => (
              <tr key={f.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`feed-row-${f.id}`}>
                <td className="p-3 font-bold">{f.name}</td>
                <td className="p-3 uppercase text-xs">{f.feed_type}</td>
                <td className="p-3 text-xs text-neutral-500 max-w-md truncate">{f.url}</td>
                <td className="p-3 text-xs">{f.default_category}</td>
                <td className="p-3">
                  {f.dry_run ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-800 px-2 py-1">Dry Run</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 px-2 py-1">Live</span>
                  )}
                </td>
                <td className="p-3 text-xs">
                  {f.last_sync_at ? new Date(f.last_sync_at).toLocaleString() : (
                    f.last_dry_run_at ? <span className="text-yellow-700">dry: {new Date(f.last_dry_run_at).toLocaleString()}</span> : "—"
                  )}
                </td>
                <td className="p-3">
                  {f.last_sync_success === true && <span className="text-xs font-bold text-green-700 uppercase">OK</span>}
                  {f.last_sync_success === false && <span className="text-xs font-bold text-red-700 uppercase">FAIL</span>}
                  {f.last_sync_success === null && <span className="text-xs text-neutral-500 uppercase">NEVER</span>}
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => previewFeed(f)} disabled={previewing === f.id} className="p-2 hover:text-[color:var(--vt-orange)] disabled:opacity-50" title="Preview (dry run)" data-testid={`preview-${f.id}`}>
                    <Eye size={16} />
                  </button>
                  <button onClick={() => syncNow(f)} disabled={syncing === f.id} className="p-2 hover:text-[color:var(--vt-orange)] disabled:opacity-50" title="Sync now" data-testid={`sync-${f.id}`}>
                    <ArrowsClockwise size={16} className={syncing === f.id ? "animate-spin" : ""} />
                  </button>
                  <button onClick={() => openLogs(f)} className="p-2 hover:text-black" title="View logs">
                    <ClipboardText size={16} />
                  </button>
                  <button onClick={() => { setEditing(f); setShowNew(true); }} className="p-2 hover:text-black" title="Edit" data-testid={`edit-feed-${f.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => del(f)} className="p-2 hover:text-red-600" title="Delete" data-testid={`del-feed-${f.id}`}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between p-6 border-b border-neutral-200">
              <h2 className="font-display font-black text-2xl">
                {editing ? "Edit Feed Source" : (importMode === "url" ? "Import from Product URL" : "New Feed Source")}
              </h2>
              <button onClick={() => { setShowNew(false); setEditing(null); setImportMode("feed"); }} data-testid="close-new-modal">✕</button>
            </div>

            {/* MODE TOGGLE — only when creating new (not editing) */}
            {!editing && (
              <div className="border-b border-neutral-200 p-4" data-testid="import-mode-toggle">
                <div className="overline text-neutral-600 mb-2">What are you importing?</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setImportMode("feed")}
                    className={`text-left p-3 border-2 transition-colors ${importMode === "feed" ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}
                    data-testid="mode-feed">
                    <div className="font-bold text-sm">📄 Data Feed</div>
                    <div className="text-[11px] text-neutral-600 mt-0.5">CSV / JSON / XML file with many rows</div>
                  </button>
                  <button type="button" onClick={() => setImportMode("url")}
                    className={`text-left p-3 border-2 transition-colors ${importMode === "url" ? "border-[color:var(--vt-orange)] bg-yellow-50" : "border-neutral-200 hover:border-neutral-400"}`}
                    data-testid="mode-url">
                    <div className="font-bold text-sm">🔗 Product URL</div>
                    <div className="text-[11px] text-neutral-600 mt-0.5">Single page from Bike24, Amazon, eBay, etc.</div>
                  </button>
                </div>
              </div>
            )}

            {/* URL IMPORT MODE — new */}
            {importMode === "url" && !editing ? (
              <form onSubmit={createFromUrl} className="p-6 space-y-3" data-testid="url-import-form">
                <div className="bg-yellow-50 border border-yellow-200 p-3 text-xs">
                  <b>Supported sites:</b> bike24.de/.com, bike-discount.de, bike-components.de, amazon.de, ebay.de, decathlon.de, tradeinn.com, zalando.de, aliexpress.com. Amazon / eBay / AliExpress / Zalando / Bike24 require a ScrapingBee API key configured on the server.
                </div>
                <div>
                  <label className="overline text-neutral-600 block mb-1">Product URL</label>
                  <input required type="url" className="vt-input" placeholder="https://www.bike-discount.de/en/shimano-xt-cs-m8100-12-speed-cassette"
                    value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="url-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-neutral-600 block mb-1">Category</label>
                    <select className="vt-input" value={form.default_category} onChange={(e) => setForm({ ...form, default_category: e.target.value })} data-testid="url-category">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline text-neutral-600 block mb-1">Brand (optional)</label>
                    <input className="vt-input" placeholder="Shimano / SRAM / Bosch..." value={urlBrand} onChange={(e) => setUrlBrand(e.target.value)} data-testid="url-brand" />
                  </div>
                </div>
                <div>
                  <label className="overline text-neutral-600 block mb-1">Markup Multiplier</label>
                  <input type="number" step="0.01" className="vt-input" value={form.markup} onChange={(e) => setForm({ ...form, markup: parseFloat(e.target.value) })} />
                  <p className="text-xs text-neutral-500 mt-1">1.30 = 30% markup applied on the scraped supplier price</p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setShowNew(false); setImportMode("feed"); }} className="vt-btn-outline text-xs" disabled={urlSubmitting}>Cancel</button>
                  <button type="submit" disabled={urlSubmitting} className="vt-btn-orange text-xs" data-testid="url-submit">
                    {urlSubmitting ? "Working…" : "Scrape & Import Product"}
                  </button>
                </div>
                {urlSubmitting && urlStatus && (
                  <div className="text-xs text-neutral-600 flex items-center gap-2 pt-2 border-t border-neutral-200 mt-2" data-testid="url-import-status">
                    <ArrowsClockwise size={12} className="animate-spin" /> {urlStatus}
                  </div>
                )}
              </form>
            ) : (
            <form onSubmit={createFeed} className="p-6 space-y-3">
              <div className="bg-neutral-50 border border-neutral-200 p-3 text-xs flex items-center justify-between gap-3">
                <span className="text-neutral-600">Need the required format? Download a starter file:</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => downloadTemplate("csv")} className="underline font-bold hover:text-[color:var(--vt-orange)]">CSV</button>
                  <span>·</span>
                  <button type="button" onClick={() => downloadTemplate("json")} className="underline font-bold hover:text-[color:var(--vt-orange)]">JSON</button>
                  <span>·</span>
                  <button type="button" onClick={() => downloadTemplate("xml")} className="underline font-bold hover:text-[color:var(--vt-orange)]">XML</button>
                </div>
              </div>
              <input required className="vt-input" placeholder="Name (e.g. Bike24 Parts CSV)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="feed-name" />
              <input required className="vt-input" placeholder="https://supplier.com/feed.csv" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="feed-url" />
              <select className="vt-input" value={form.feed_type} onChange={(e) => setForm({ ...form, feed_type: e.target.value })} data-testid="feed-type">
                {FEED_TYPES.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
              </select>
              <select className="vt-input" value={form.default_category} onChange={(e) => setForm({ ...form, default_category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div>
                <label className="overline text-neutral-500 block mb-1">Markup Multiplier</label>
                <input type="number" step="0.01" className="vt-input" value={form.markup} onChange={(e) => setForm({ ...form, markup: parseFloat(e.target.value) })} />
                <p className="text-xs text-neutral-500 mt-1">1.30 = 30% markup on supplier price</p>
              </div>
              <div>
                <label className="overline text-neutral-500 block mb-1">Price Change Alert Threshold</label>
                <input type="number" step="0.01" min="0" max="1" className="vt-input" value={form.price_change_threshold} onChange={(e) => setForm({ ...form, price_change_threshold: parseFloat(e.target.value) })} data-testid="feed-threshold" />
                <p className="text-xs text-neutral-500 mt-1">0.10 = flag any product whose supplier price shifts by ≥ 10%</p>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (include in daily sync)</label>
              <label className="flex items-start gap-2 text-sm bg-yellow-50 border border-yellow-200 p-3">
                <input type="checkbox" checked={form.dry_run} onChange={(e) => setForm({ ...form, dry_run: e.target.checked })} data-testid="feed-dry-run" className="mt-0.5" />
                <div>
                  <div className="font-bold">Dry Run Mode</div>
                  <div className="text-xs text-neutral-600">When enabled, scheduled syncs will only report what would change — nothing is written to the catalog. Perfect for validating a new feed before going live.</div>
                </div>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowNew(false); setEditing(null); }} className="vt-btn-outline text-xs">Cancel</button>
                <button type="submit" className="vt-btn-orange text-xs" data-testid="feed-save">{editing ? "Save Changes" : "Create Feed"}</button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {showLogs && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex justify-between p-6 border-b border-neutral-200">
              <h2 className="font-display font-black text-2xl">Sync Logs — {showLogs.name}</h2>
              <button onClick={() => setShowLogs(null)}>✕</button>
            </div>
            <div className="p-6 space-y-2">
              {logs.length === 0 ? <p className="text-neutral-500 text-sm">No syncs yet.</p> :
                logs.map((l, i) => (
                  <div key={i} className="border border-neutral-200 p-4">
                    <div className="flex justify-between mb-1">
                      <div className="text-xs text-neutral-500">{new Date(l.started_at).toLocaleString()}</div>
                      <div className={`text-xs font-bold uppercase ${l.success ? "text-green-700" : "text-red-700"}`}>{l.success ? "OK" : "FAIL"}</div>
                    </div>
                    {l.success ? (
                      <div className="text-sm">+{l.created} new, {l.updated} updated · {l.count} items processed</div>
                    ) : (
                      <div className="text-sm text-red-700">{l.error}</div>
                    )}
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="preview-modal">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex justify-between items-center p-6 border-b border-neutral-200 z-10">
              <div>
                <div className="overline text-[color:var(--vt-orange)] mb-1">DRY RUN · {preview.feed?.name}</div>
                <h2 className="font-display font-black text-2xl">Sync Preview</h2>
              </div>
              <button onClick={closePreview}>✕</button>
            </div>
            <div className="p-6 space-y-6">
              {!preview.success ? (
                <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="preview-error">
                  Feed fetch failed: {preview.error || "unknown error"}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-neutral-200 p-4">
                      <div className="overline text-neutral-500 mb-1">Fetched</div>
                      <div className="font-display font-black text-2xl">{preview.count ?? 0}</div>
                    </div>
                    <div className="border border-green-200 bg-green-50 p-4" data-testid="preview-would-create">
                      <div className="overline text-green-700 mb-1">Would Create</div>
                      <div className="font-display font-black text-2xl text-green-700">+{preview.would_create_count ?? 0}</div>
                    </div>
                    <div className="border border-blue-200 bg-blue-50 p-4" data-testid="preview-would-update">
                      <div className="overline text-blue-700 mb-1">Would Update</div>
                      <div className="font-display font-black text-2xl text-blue-700">{preview.would_update_count ?? 0}</div>
                    </div>
                    <div className="border border-yellow-200 bg-yellow-50 p-4" data-testid="preview-price-changes">
                      <div className="overline text-yellow-800 mb-1">Price Alerts</div>
                      <div className="font-display font-black text-2xl text-yellow-800">{preview.price_changes?.length ?? 0}</div>
                    </div>
                  </div>

                  {preview.price_changes?.length > 0 && (
                    <div>
                      <h3 className="font-display font-bold text-lg mb-3">Significant Price Changes (≥ {Math.round((preview.feed.price_change_threshold ?? 0.10) * 100)}%)</h3>
                      <div className="border border-yellow-200 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-yellow-50 border-b border-yellow-200">
                            <tr>
                              <th className="text-left p-3 overline text-neutral-700">Product</th>
                              <th className="text-right p-3 overline text-neutral-700">Old Retail</th>
                              <th className="text-right p-3 overline text-neutral-700">New Retail</th>
                              <th className="text-right p-3 overline text-neutral-700">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.price_changes.map((c, i) => (
                              <tr key={i} className="border-b border-yellow-100">
                                <td className="p-3">
                                  <div className="font-bold">{c.name}</div>
                                  <div className="text-[10px] text-neutral-500">{c.slug}</div>
                                </td>
                                <td className="p-3 text-right text-neutral-500">€{c.old_retail?.toFixed(2)}</td>
                                <td className="p-3 text-right font-bold">€{c.new_retail?.toFixed(2)}</td>
                                <td className={`p-3 text-right font-bold ${c.delta_pct > 0 ? "text-red-700" : "text-green-700"}`}>
                                  {c.delta_pct > 0 ? "+" : ""}{c.delta_pct?.toFixed(2)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {preview.would_create?.length > 0 && (
                    <details className="border border-neutral-200">
                      <summary className="cursor-pointer p-4 font-bold text-sm hover:bg-neutral-50">New products ({preview.would_create.length}) →</summary>
                      <div className="p-4 border-t border-neutral-200 space-y-1 text-sm max-h-64 overflow-y-auto">
                        {preview.would_create.map((p, i) => (
                          <div key={i} className="flex justify-between border-b border-neutral-100 py-1">
                            <span>{p.name}</span>
                            <span className="font-bold text-[color:var(--vt-orange)]">€{p.retail_price?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {preview.errors?.length > 0 && (
                    <details className="border border-red-200">
                      <summary className="cursor-pointer p-4 font-bold text-sm text-red-700 hover:bg-red-50">Parse errors ({preview.errors.length}) →</summary>
                      <div className="p-4 border-t border-red-200 space-y-1 text-xs font-mono max-h-64 overflow-y-auto">
                        {preview.errors.map((e, i) => (
                          <div key={i}>row {e.index}: {e.error}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-neutral-200 p-4 flex justify-end gap-2">
              <button onClick={closePreview} className="vt-btn-outline text-xs">Close</button>
              {preview.success && (preview.would_create_count > 0 || preview.would_update_count > 0) && (
                <button onClick={applyFromPreview} className="vt-btn-orange text-xs" data-testid="preview-apply">
                  Apply Changes (Sync Now)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
